import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ThemeToggle } from '@/components/ui/theme-toggle';
import { useAuth } from '../../context/AuthContext';
import { useBrand } from '../../context/BrandContext';
import { Menu, X, Scissors, LogIn, UserPlus, LayoutDashboard, LogOut, Sparkles, Store } from 'lucide-react';
import { createPortal } from 'react-dom';

export function MobileMenu() {
  const [isOpen, setIsOpen] = useState(false);
  const { user, logout } = useAuth();
  const { isSingleStore, brand } = useBrand();
  const navigate = useNavigate();
  const isAuthenticated = !!user;

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleUserRedirect = () => {
    setIsOpen(false);
    if (user?.role === 'ADMIN_PLATFORM') navigate('/admin/platform');
    else if (user?.role === 'ADMIN_STORE') navigate('/admin/store');
    else if (user?.role === 'BARBER') navigate('/dashboard/barber');
    else navigate('/dashboard');
  };

  const handleLogout = () => {
    logout();
    setIsOpen(false);
    navigate('/');
  };

  const menuContent = isOpen ? createPortal(
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black/70 backdrop-blur-sm"
        style={{ zIndex: 9998 }}
        onClick={() => setIsOpen(false)}
      />

      {/* Sidebar */}
      <div 
        className="fixed top-0 right-0 h-full w-80 max-w-[85vw] bg-background shadow-2xl"
        style={{ 
          zIndex: 9999,
          animation: 'slideIn 0.3s ease-out'
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            {brand?.logoUrl ? (
              <img src={brand.logoUrl} alt={brand.appName} className="w-6 h-6 rounded object-cover" />
            ) : (
              <Scissors className="w-6 h-6 text-primary" />
            )}
            <span className="font-bold text-lg">{brand?.appName || 'AppLook'}</span>
          </div>
          <div className="flex items-center gap-1">
            <ThemeToggle />
            <button
              onClick={() => setIsOpen(false)}
              className="p-2 hover:bg-muted rounded-lg transition-colors"
              aria-label="Chiudi menu"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* User Info (if authenticated) */}
        {isAuthenticated && (
          <div className="p-4 border-b bg-primary/5">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Connesso come</p>
            <p className="font-semibold truncate mt-1">{user.name}</p>
            <p className="text-sm text-muted-foreground truncate">{user.email}</p>
          </div>
        )}

        {/* Navigation Links */}
        <nav className="p-4 space-y-1">
          {/* Funzionalità - only in multi-store mode */}
          {!isSingleStore && (
            <a 
              href="#features" 
              onClick={() => setIsOpen(false)}
              className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-muted transition-colors text-base"
            >
              <Sparkles className="w-5 h-5 text-muted-foreground" />
              Funzionalità
            </a>
          )}
          
          {/* Negozi - only in multi-store mode */}
          {!isSingleStore && (
            <a 
              href="#stores" 
              onClick={() => setIsOpen(false)}
              className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-muted transition-colors text-base"
            >
              <Store className="w-5 h-5 text-muted-foreground" />
              Negozi
            </a>
          )}
        </nav>

        {/* Auth Buttons */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t bg-background space-y-3">
          {isAuthenticated ? (
            <>
              <Button 
                className="w-full h-12 justify-start gap-3 text-base" 
                onClick={handleUserRedirect}
                data-testid="mobile-dashboard-btn"
              >
                <LayoutDashboard className="w-5 h-5" />
                Dashboard
              </Button>
              <Button 
                variant="outline" 
                className="w-full h-12 justify-start gap-3 text-base text-destructive hover:text-destructive"
                onClick={handleLogout}
                data-testid="mobile-logout-btn"
              >
                <LogOut className="w-5 h-5" />
                Esci
              </Button>
            </>
          ) : (
            <>
              <Button 
                variant="outline" 
                className="w-full h-12 justify-start gap-3 text-base" 
                asChild
                data-testid="mobile-login-btn"
              >
                <Link to="/login" onClick={() => setIsOpen(false)}>
                  <LogIn className="w-5 h-5" />
                  Accedi
                </Link>
              </Button>
              <Button 
                className="w-full h-12 justify-start gap-3 text-base" 
                asChild
                data-testid="mobile-register-btn"
              >
                <Link to="/register" onClick={() => setIsOpen(false)}>
                  <UserPlus className="w-5 h-5" />
                  Registrati
                </Link>
              </Button>
            </>
          )}
        </div>
      </div>

      <style>{`
        @keyframes slideIn {
          from {
            transform: translateX(100%);
          }
          to {
            transform: translateX(0);
          }
        }
      `}</style>
    </>,
    document.body
  ) : null;

  return (
    <>
      {/* Hamburger Button - Only visible on mobile */}
      <button
        onClick={() => setIsOpen(true)}
        className="md:hidden p-2 hover:bg-muted rounded-lg transition-colors"
        aria-label="Apri menu"
        data-testid="mobile-menu-btn"
      >
        <Menu className="w-6 h-6" />
      </button>

      {menuContent}
    </>
  );
}

export default MobileMenu;
